import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Loader2, Mail, Phone, MessageSquare, ArrowLeft, CheckCheck } from "lucide-react";
import { buildAdPath } from "@/lib/slugs";

/**
 * MyAdsFeedbackPage (Iter-237) — sellers view every feedback lead sent
 * against any of their ads. Backend endpoint auto-scopes to the signed-in
 * seller (`GET /api/classifieds/my/listing-feedback`).
 */
export default function MyAdsFeedbackPage() {
  const q = useQuery({
    queryKey: ["my-listing-feedback"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/my/listing-feedback");
      return data;
    },
    staleTime: 30_000,
  });

  const items = q.data?.items || [];
  const total = q.data?.total ?? items.length;

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="my-ads-feedback-page">
      <Seo title="Feedback inbox · My Ads" />
      <Breadcrumbs
        items={[
          { name: "Home", href: "/" },
          { name: "My Ads", href: "/my-ads" },
          { name: "Feedback" },
        ]}
      />
      <div className="flex flex-wrap items-end justify-between gap-4 mt-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">
            Feedback inbox
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every message a buyer sent about your listings.
          </p>
        </div>
        <Link
          to="/my-ads"
          className="inline-flex items-center gap-1.5 rounded-full border border-soft bg-white hover:bg-slate-50 text-slate-700 font-semibold px-4 py-1.5 text-xs"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to My Ads
        </Link>
      </div>

      {q.isLoading ? (
        <div className="mt-8 flex justify-center">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      ) : total === 0 ? (
        <div
          data-testid="my-ads-feedback-empty"
          className="mt-6 rounded-3xl bg-white shadow-card p-10 text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 text-slate-400 grid place-items-center">
            <MessageSquare className="w-7 h-7" />
          </div>
          <div className="mt-3 font-display font-bold text-slate-900 text-lg">
            No feedback yet
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Buyer messages sent from your ad pages will land here.
          </p>
        </div>
      ) : (
        <ul className="mt-6 space-y-3" data-testid="my-ads-feedback-list">
          {items.map((it, i) => (
            <li
              key={it.id || i}
              data-testid={`my-ads-feedback-item-${i}`}
              className="rounded-2xl bg-white shadow-card p-5"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="font-semibold text-slate-800">{it.name || "Anonymous"}</div>
                <div className="text-[11px] text-slate-400">
                  {it.created_at
                    ? new Date(it.created_at).toLocaleString("en-NZ", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })
                    : ""}
                </div>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
                {it.email && (
                  <a
                    href={`mailto:${it.email}`}
                    className="inline-flex items-center gap-1 text-[color:var(--brand-blue)] hover:underline"
                    data-testid={`my-ads-feedback-email-${i}`}
                  >
                    <Mail className="w-3.5 h-3.5" /> {it.email}
                  </a>
                )}
                {it.mobile && (
                  <a
                    href={`tel:${it.mobile}`}
                    className="inline-flex items-center gap-1 text-[color:var(--brand-blue)] hover:underline"
                    data-testid={`my-ads-feedback-mobile-${i}`}
                  >
                    <Phone className="w-3.5 h-3.5" /> {it.mobile}
                  </a>
                )}
                {it.contact_ok && (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                    <CheckCheck className="w-3.5 h-3.5" /> Open to other advertisers
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">
                {it.message}
              </p>
              {it.listing_id && (
                <div className="mt-3">
                  <Link
                    to={buildAdPath({ id: it.listing_id, title: it.listing_title })}
                    className="text-[11px] font-semibold text-[color:var(--brand-blue)] hover:underline"
                    data-testid={`my-ads-feedback-listing-${i}`}
                  >
                    View listing →
                  </Link>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
